import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  History, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ChevronDown, 
  ChevronUp,
  TrendingUp,
  AlertTriangle,
  User,
  FileText,
  Target
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Decision badge colors
const getDecisionStyle = (decision) => {
  switch (decision) {
    case 'GO':
      return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 };
    case 'NO_GO':
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: XCircle };
    case 'PURSUING':
    default:
      return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: Clock };
  }
};

// Risk level badge
const getRiskStyle = (risk) => {
  switch (risk?.toUpperCase()) {
    case 'HIGH':
      return 'bg-red-500 text-white';
    case 'MEDIUM':
      return 'bg-amber-500 text-white';
    case 'LOW':
      return 'bg-emerald-500 text-white';
    default:
      return 'bg-slate-200 text-slate-600';
  }
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Single History Item
function HistoryItem({ item, isLatest }) {
  const [isExpanded, setIsExpanded] = useState(isLatest);
  const style = getDecisionStyle(item.decision);
  const Icon = style.icon;
  
  return (
    <div 
      className={cn(
        "border rounded-lg overflow-hidden transition-all",
        isLatest ? "border-blue-300 shadow-sm" : "border-slate-200"
      )}
      data-testid={`decision-history-item-${item.id}`}
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center",
            style.bg
          )}>
            <Icon className={cn("w-5 h-5", style.text)} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={cn("font-semibold", style.text)}>
                {item.decision?.replace('_', '-')}
              </span>
              {isLatest && (
                <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                  Latest
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {item.decision_by} • {formatDate(item.decision_date)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {item.pwin_at_decision && (
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900">{item.pwin_at_decision}%</p>
              <p className="text-xs text-slate-500">pWin</p>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50">
          {/* Decision Notes */}
          {item.decision_notes && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Decision Notes</span>
              </div>
              <p className="text-sm text-slate-600 bg-white p-3 rounded border border-slate-200">
                {item.decision_notes}
              </p>
            </div>
          )}
          
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* pWin Score */}
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500">pWin Score</span>
              </div>
              <p className="text-lg font-bold text-slate-900">
                {item.pwin_at_decision ? `${item.pwin_at_decision}%` : '—'}
              </p>
            </div>
            
            {/* Risk Level */}
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-slate-500">Risk Level</span>
              </div>
              <Badge className={cn("text-xs", getRiskStyle(item.risk_at_decision))}>
                {item.risk_at_decision || 'N/A'}
              </Badge>
            </div>
            
            {/* Compliance */}
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-slate-500">Compliance</span>
              </div>
              <p className="text-lg font-bold text-slate-900">
                {item.compliance_percent ? `${item.compliance_percent}%` : '—'}
              </p>
            </div>
            
            {/* Requirements */}
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-slate-500">Requirements</span>
              </div>
              <p className="text-lg font-bold text-slate-900">
                {item.gaps?.requirements_count || 0}
              </p>
            </div>
          </div>
          
          {/* 5-Dimension Scores */}
          {item.scores && (
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">5-Dimension Scores</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(item.scores).map(([key, value]) => (
                  <Badge 
                    key={key} 
                    variant="outline" 
                    className="text-xs bg-white"
                  >
                    {key.replace('_', ' ')}: <span className="font-semibold ml-1">{value || '—'}</span>/5
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Gap Analysis Summary */}
          {item.gaps && (item.gaps.met > 0 || item.gaps.partial > 0 || item.gaps.open > 0) && (
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">Gap Analysis at Decision</p>
              <div className="flex gap-4">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                  <span className="text-xs text-slate-600">Met: {item.gaps.met}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-amber-400 rounded"></div>
                  <span className="text-xs text-slate-600">Partial: {item.gaps.partial}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-400 rounded"></div>
                  <span className="text-xs text-slate-600">Open: {item.gaps.open}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Loading skeleton
function HistorySkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2].map((i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div>
              <Skeleton className="w-24 h-5 mb-1" />
              <Skeleton className="w-40 h-3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Main Component
export default function DecisionHistory({ pursuitId, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchHistory = async () => {
      if (!pursuitId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `${API_URL}/api/pursuits/${pursuitId}/decision-history`,
          { headers: getAuthHeaders() }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch decision history');
        }
        
        const data = await response.json();
        setHistory(data.history || []);
      } catch (err) {
        console.error('Error fetching decision history:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchHistory();
  }, [pursuitId, getAuthHeaders]);
  
  return (
    <Card className="border-slate-200 bg-white shadow-sm" data-testid="decision-history-panel">
      <CardHeader className="pb-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg font-semibold text-slate-900">
              Decision History
            </CardTitle>
          </div>
          {history.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {history.length} decision{history.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Historical record of Go/No-Go decisions and their context
        </p>
      </CardHeader>
      
      <CardContent className="pt-4">
        {isLoading ? (
          <HistorySkeleton />
        ) : error ? (
          <div className="text-center py-6">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-sm text-red-600">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-1">No decisions recorded yet</p>
            <p className="text-xs text-slate-400">
              Decisions will appear here when you click Go, No-Go, or Pursuing
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
            {history.map((item, index) => (
              <HistoryItem 
                key={item.id} 
                item={item} 
                isLatest={index === 0} 
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
